import { Alert, Autocomplete, Box, CircularProgress, Switch, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import Request from "../Utils/Request/Request";
import { getErrorMessage, showToast } from "../Utils";

function MaintenanceMode() {
  const [enabled, setEnabled] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await Request.get("/maintenance-mode");
      setEnabled(Boolean(response.data.enabled));
      setUsers(response.data.users || []);
    } catch (error) {
      showToast("error", getErrorMessage(error, "Unable to load maintenance settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateEnabled = async (nextEnabled) => {
    setSaving(true);
    try {
      await Request.put("/maintenance-mode", { enabled: nextEnabled });
      setEnabled(nextEnabled);
      showToast("success", `Maintenance Mode is now ${nextEnabled ? "ON" : "OFF"}.`);
    } catch (error) {
      showToast("error", getErrorMessage(error, "Unable to update Maintenance Mode."));
    } finally {
      setSaving(false);
    }
  };

  const updateExcludedUsers = async (nextUsers) => {
    const selectedIds = new Set(nextUsers.map((user) => user.id));
    const changedUsers = users.filter((user) => Boolean(user.maintenance_mode_exempt) !== selectedIds.has(user.id));
    setSaving(true);
    try {
      await Promise.all(changedUsers.map((user) => Request.put(`/maintenance-mode/exclusions/${user.id}`, {
        exempt: selectedIds.has(user.id),
      })));
      setUsers((current) => current.map((user) => ({ ...user, maintenance_mode_exempt: selectedIds.has(user.id) })));
    } catch (error) {
      showToast("error", getErrorMessage(error, "Unable to update excluded users."));
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: "grid", placeItems: "center", minHeight: 300 }}><CircularProgress /></Box>;
  const excludedUsers = users.filter((user) => user.maintenance_mode_exempt);

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", p: 2 }}>
      <Typography variant="h4" fontWeight={700} color="#365b6d">Maintenance Mode</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>Block system access while updates are being deployed. Admins and selected users remain able to test the system.</Typography>
      <Alert severity={enabled ? "warning" : "success"} sx={{ mb: 3 }}>
        Maintenance Mode is currently <strong>{enabled ? "ON" : "OFF"}</strong>.
      </Alert>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, border: "1px solid #dce4e8", borderRadius: 2, mb: 3 }}>
        <Box><Typography fontWeight={700}>Enable Maintenance Mode</Typography><Typography variant="body2" color="text.secondary">Non-excluded users are shown the maintenance page.</Typography></Box>
        <Switch checked={enabled} disabled={saving} onChange={(event) => updateEnabled(event.target.checked)} />
      </Box>
      <Autocomplete
        multiple
        options={users}
        value={excludedUsers}
        disabled={saving}
        onChange={(_, value) => updateExcludedUsers(value)}
        getOptionLabel={(user) => `${user.fullName || user.username} (${user.username})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label="Excluded users" placeholder="Search and select users" helperText="Excluded users can log in and use the system during maintenance." />}
      />
    </Box>
  );
}

export default MaintenanceMode;
